import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { useGetBackToTimeLogPage } from '@legacy/stores/PageStore/pageStore.zustand';
import { IconButton } from '@mui/material';
import { useUserHasAccessToPersonalPlan } from '@legacy/apis/reportingWidgetsAPI';
import { Heading } from '@legacy/components/ui-components/Heading';
import { TextLink } from '@legacy/components/ui-components/Link';
import { FeatureControl } from '@legacy/components/utils/FeatureControl';
import { Navigation } from './components/Navigation';
import { MainSearch } from './components/MainSearch';
import { TimeTrackerButton } from './components/TimeTrackerButton';
import { HelpButton } from './components/HelpButton';
import { SignUpButton } from './components/SignUpButton';
import { HeaderBase } from './HeaderBase';
import { ResponseHandler } from '../../utils/ResponseHandler';
import { Icon } from '../../ui-components';

import styles from './Header.module.scss';

export const HeaderWithNavigation = () => {
  const { pathname } = useLocation();
  const { hasAccessToPersonalPlan, isLoading, isError } =
    useUserHasAccessToPersonalPlan();
  const { t } = useTranslation('personalPlan');

  return (
    <HeaderBase
      primaryArea={<Navigation />}
      optionsArea={
        <>
          <SignUpButton />
          <ResponseHandler isLoading={isLoading} isError={isError}>
            {hasAccessToPersonalPlan ? (
              <IconButton
                title={t('HeadingText')}
                data-automation-id="OpenPersonalPlanModalButton"
                size="medium"
                component={Link}
                to="/personal-plan"
                state={{ background: pathname }}
              >
                <Icon iconName="resourceManagement" size="large" />
              </IconButton>
            ) : null}
          </ResponseHandler>
          <FeatureControl
            indexControlName="TimeTracker"
            userFeatureKey="useStopwatchTimeTracking"
            pageIdentifierKey="TimeTrackingBase"
          >
            <TimeTrackerButton />
          </FeatureControl>
          <FeatureControl indexControlName="Search">
            <MainSearch />
          </FeatureControl>
          <FeatureControl indexControlName="Users">
            <HelpButton data-automation-id="HeaderHelpSection" />
          </FeatureControl>
        </>
      }
      data-automation-id="HeaderWithNavigation"
    />
  );
};

interface HeaderWithHeadingProps {
  heading: string;
  isHelpVisible?: boolean;
}

export const HeaderWithHeading = ({
  heading,
  isHelpVisible = true,
}: HeaderWithHeadingProps) => {
  const { t } = useTranslation('header');

  const backToTimeLogUrl = useGetBackToTimeLogPage();
  return (
    <HeaderBase
      primaryArea={
        <div className={styles.Heading}>
          <Heading
            level={1}
            as={2}
            medium
            data-automation-id="HeaderWithHeading"
          >
            {heading}
          </Heading>
        </div>
      }
      optionsArea={
        <>
          <TextLink
            href={backToTimeLogUrl}
            className={styles.BackLink}
            data-automation-id="HeaderGoBack"
            useTextColor
          >
            {t('SettingsPagesBackToTimeLogLink')}
          </TextLink>
          {isHelpVisible ? (
            <FeatureControl indexControlName="Users">
              <HelpButton data-automation-id="HeaderHelpSection" />
            </FeatureControl>
          ) : null}
        </>
      }
    />
  );
};
